import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, MessageCircle } from 'lucide-react';
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  VIBER_CHAT_URL,
  WHATSAPP_CHAT_URL,
} from '@/constants/delivery';
import { Separator } from '../ui/separator';
import brandLogo from '@/assets/logo.png';

const socialIconClass =
  'text-primary-foreground/60 hover:text-gold transition-colors duration-200';

function ViberIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.294 4.177.528 6.488.483 9.817c-.046 3.393.54 5.997 1.96 8.048l-1.06 3.95 4.179-1.098c1.634.897 3.548 1.326 5.692 1.326h.005c5.662 0 10.066-4.62 10.066-10.314C21.325 4.62 17.034.002 11.398.002zm.057 18.51h-.004c-1.82 0-3.608-.49-5.17-1.416l-.37-.22-2.472.648.66-2.454-.242-.388c-1.01-1.622-1.54-3.49-1.536-5.6.04-4.74 3.88-8.6 8.63-8.6 2.303 0 4.467.897 6.09 2.527a8.52 8.52 0 0 1 2.52 6.088c-.003 4.74-3.874 8.615-8.616 8.615zm4.82-6.483c-.264-.132-1.566-.773-1.808-.86-.242-.09-.418-.132-.594.132-.176.264-.682.86-.836 1.036-.154.176-.308.198-.572.066-.264-.132-1.114-.41-2.122-1.307-.784-.698-1.314-1.56-1.468-1.824-.154-.264-.017-.406.116-.538.12-.12.264-.308.396-.462.132-.154.176-.264.264-.44.088-.176.044-.33-.022-.462-.066-.132-.594-1.432-.814-1.962-.214-.51-.432-.44-.594-.448l-.506-.01c-.176 0-.462.066-.594.33-.132.264-.506.462-.506 1.144 0 .682.506 1.342.576 1.432.066.088.994 1.518 2.406 2.13.336.145.598.232.802.297.337.107.644.092.886.056.27-.04 1.566-.64 1.786-1.254.22-.614.22-1.142.154-1.254-.066-.11-.242-.176-.506-.308z" />
    </svg>
  );
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-footer">
      {/* Gold divider */}
      <div className="divider-gold" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <img
              src={brandLogo}
              alt="Sjenički proizvodi Fazlić"
              className="h-20 w-auto mb-4 object-contain"
            />
            <p className="text-primary-foreground/70 text-sm leading-relaxed font-body-serif">
              Sjenički Proizvodi Fazlić - porodični brend sa Sjeničke visoravni.
              Pravimo domaće, prirodne i autentične proizvode sa tradicijom dugom decenijama.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a href="#" className={socialIconClass} aria-label="Facebook">
                <Facebook size={18} />
              </a>
              <a
                href="https://instagram.com/sjenicki_proizvodii"
                target="_blank"
                rel="noreferrer"
                className={socialIconClass}
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a href={VIBER_CHAT_URL} className={socialIconClass} aria-label="Viber">
                <ViberIcon />
              </a>
              <a
                href={WHATSAPP_CHAT_URL}
                target="_blank"
                rel="noreferrer"
                className={socialIconClass}
                aria-label="WhatsApp"
              >
                <WhatsAppIcon />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-serif text-primary-foreground font-semibold mb-4 text-base">Brza navigacija</h4>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: 'Početna' },
                { to: '/proizvodi', label: 'Svi Proizvodi' },
                { to: '/kontakt', label: 'Kontakt' },
                { to: '/korpa', label: 'Korpa' },
              ].map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-primary-foreground/65 hover:text-gold transition-colors duration-200 text-sm font-ui"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-primary-foreground font-semibold mb-4 text-base">Kontakt</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-primary-foreground/65 text-sm font-ui">
                <Phone size={15} className="mt-0.5 shrink-0 text-gold/70" />
                <a href={CONTACT_PHONE_TEL} className="hover:text-gold transition-colors">
                  {CONTACT_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/65 text-sm font-ui">
                <MessageCircle size={15} className="mt-0.5 shrink-0 text-gold/70" />
                <span className="flex flex-col gap-1">
                  <a href={VIBER_CHAT_URL} className="hover:text-gold transition-colors">Viber</a>
                  <a href={WHATSAPP_CHAT_URL} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors">
                    WhatsApp
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/65 text-sm font-ui">
                <Mail size={15} className="mt-0.5 shrink-0 text-gold/70" />
                <span>drndicnermina3@gmail.com</span>
              </li>
              <li className="flex items-start gap-3 text-primary-foreground/65 text-sm font-ui">
                <MapPin size={15} className="mt-0.5 shrink-0 text-gold/70" />
                <span>Izeta Čavića, Prijepolje</span>
              </li>
            </ul>
            <div className="mt-5 p-3 bg-primary-foreground/5 rounded-lg border border-primary-foreground/10">
              <p className="text-primary-foreground/60 text-xs font-ui leading-relaxed">
                Radno vreme: Pon–Sub 08:00–18:00
              </p>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-primary-foreground/10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <a
            href="https://www.limtech.solutions"
            target="_blank"
            rel="noreferrer"
            className="text-primary-foreground/30 hover:text-gold text-xs font-ui transition-colors duration-200"
          >
            © {currentYear} Powered by Limtech Solutions.
          </a>
          <div className="flex items-center gap-4">
            <Link
              to="/politika-privatnosti"
              className="text-primary-foreground/40 hover:text-gold text-xs font-ui transition-colors duration-200"
            >
              Politika privatnosti
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-settings'))}
              className="text-primary-foreground/40 hover:text-gold text-xs font-ui transition-colors duration-200"
            >
              Podešavanja kolačića
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
